<template>
  <section class="live-rail" :style="railStyle">
    <GlyphView :rail="rail" />
    <div class="title-container">
      <h3 class="rail-title" v-if="title">{{ title }}</h3>
      <p class="rail-subtitle" v-if="subtitle">{{ subtitle }}</p>
    </div>
    <HorizontalScroller
      :container-id="containerId"
      :items-prefix="`rail-${props.railIndex}`"
      :items="items"
      @handleUp="handleUp"
      @handleDown="handleDown"
      @handleLeftFallback="handleLeftFallback"
    />
  </section>
</template>

<script setup lang="ts">

import { computed } from 'vue';
import { isSupportedRailItem, RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import HorizontalScroller from '@/components/containers/HorizontalScroller.vue';
import GlyphView  from '@/components/glyphs/GlyphView.vue';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { containerId, handleUp, handleDown, handleLeftFallback } = useRailNavigation(props);

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const localised = (en: string | null, ga: string | null) => {
  const value = currentLanguage.value === Language.Irish ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));

const railStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));

const items = computed(() => props.rail.items.filter(isSupportedRailItem));
</script>

<style scoped lang="scss">
.live-rail {
  position: relative;
  z-index: 0;
  padding-top: 40px;
  padding-bottom: 40px;
  padding-left: $rail-padding-left;
  padding-right: $rail-padding-right;
}

.title-container {
  text-align: left;
}

.rail-title {
  @include text-rail-title;
}

.rail-subtitle {
  @include text-rail-subtitle;
}
</style>
